import React from 'react';
import { Box, Container, Typography, Chip, Button, Avatar, Divider, Tooltip } from '@mui/material';
import { motion } from 'framer-motion';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getBlogBySlug, getRelatedBlogs, getCategoryById, getRecentBlogs } from '../data/blogData';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ShareIcon from '@mui/icons-material/Share';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { useTenantConfig } from '../hooks/useTenant';

const ACCENT = '#03c0ff';

// Slug oluşturucu (ID için)
const slugify = (text) => {
  return text
    .toLowerCase()
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

// Basit Markdown Parser
const parseContent = (content) => {
  if (!content) return '';
  
  return content
    .split('\n\n')
    .map((block, i) => {
      // Heading 2 - ID ekle
      if (block.startsWith('## ')) {
        const title = block.replace('## ', '');
        const id = slugify(title);
        return `<h2 id="${id}" key="${i}">${title}</h2>`;
      }
      // Heading 3
      if (block.startsWith('### ')) {
        const title = block.replace('### ', '');
        const id = slugify(title);
        return `<h3 id="${id}" key="${i}">${title}</h3>`;
      }
      // List items
      if (block.includes('\n- ')) {
        const items = block.split('\n- ').filter(Boolean);
        return `<ul>${items.map(item => `<li>${item.replace('- ', '')}</li>`).join('')}</ul>`;
      }
      // Single list item
      if (block.startsWith('- ')) {
        return `<ul><li>${block.replace('- ', '')}</li></ul>`;
      }
      // Paragraph
      return `<p>${block}</p>`;
    })
    .join('');
};

// Table of Contents Item
const TOCItem = ({ title, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      py: 1,
      px: 2,
      borderLeft: '2px solid',
      borderColor: active ? ACCENT : '#e2e8f0',
      color: active ? ACCENT : '#64748b',
      fontSize: '0.85rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        color: ACCENT,
        borderColor: ACCENT,
        background: 'rgba(3, 192, 255, 0.05)',
      },
    }}
  >
    {title}
  </Box>
);

// İlgili Blog Kartı - Minimal
const RelatedCard = ({ post }) => {
  
  return (
    <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          p: 2.5,
          borderRadius: 2,
          transition: 'all 0.2s ease',
          '&:hover': {
            background: '#f8fafc',
            '& .related-title': {
              color: ACCENT,
            },
          },
        }}
      >
        {/* Emoji/Image */}
        <Box
          component={'img'}
          src={post.image}
          sx={{
            width: '100px',
            height: '100px',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            flexShrink: 0,
          }}
        />
        
        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            className="related-title"
            sx={{
              fontWeight: 600,
              fontSize: '0.95rem',
              color: '#0f172a',
              lineHeight: 1.4,
              mb: 0.5,
              transition: 'color 0.2s ease',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {post.title}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8' }}>
            {post.readTime}
          </Typography>
        </Box>
      </Box>
    </Link>
  );
};

const BlogDetailPage = () => {
  const {tenantName} = useTenantConfig();
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getBlogBySlug(slug, tenantName);
  const relatedPosts = getRelatedBlogs(slug, 3, tenantName);
  const recentPosts = getRecentBlogs(4, tenantName).filter(p => p.slug !== slug).slice(0, 3);
  const category = post ? getCategoryById(post.category) : null;

  if (!post) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', pt: 10 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: '5rem', mb: 2 }}>📝</Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', mb: 1 }}>
            Yazı bulunamadı
          </Typography>
          <Typography sx={{ color: '#64748b', mb: 4 }}>
            Aradığınız içerik mevcut değil veya kaldırılmış olabilir.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/blog')}
            sx={{ 
              background: ACCENT, 
              textTransform: 'none',
              px: 4,
              '&:hover': { background: '#02a8e0' }
            }}
          >
            Blog'a Dön
          </Button>
        </Box>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Scroll to heading
  const scrollToHeading = (title) => {
    const id = slugify(title);
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Navbar yüksekliği için offset
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // İçerikten başlıkları çıkar (TOC için)
  const headings = post.content
    .split('\n')
    .filter(line => line.startsWith('## '))
    .map(line => line.replace('## ', ''));

  return (
    <Box sx={{ minHeight: '100vh', background: '#fff' }}>
      {/* Breadcrumb & Back */}
      <Box sx={{ background: '#fafbfc', borderBottom: '1px solid #f1f5f9' }}>
        <Container maxWidth="lg">
          <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              onClick={() => navigate('/blog')}
              startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
              sx={{
                color: '#64748b',
                fontWeight: 500,
                fontSize: '0.85rem',
                textTransform: 'none',
                '&:hover': { color: ACCENT, background: 'transparent' },
              }}
            >
              Blog
            </Button>
            <Typography sx={{ color: '#cbd5e1' }}>/</Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
              {category?.name}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Hero */}
      <Box sx={{ pt: { xs: 6, md: 8 }, pb: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Category Badge */}
            <Box sx={{ mb: 3 }}>
              <Chip
                label={`${category?.name}`}
                sx={{
                  px: 1,
                  py: 2,
                  borderRadius: 2,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  background: '#f1f5f9',
                  color: '#475569',
                }}
              />
            </Box>

            {/* Title */}
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 800,
                color: '#0f172a',
                letterSpacing: '-0.03em',
                lineHeight: 1.2,
                mb: 3,
              }}
            >
              {post.title}
            </Typography>

            {/* Excerpt */}
            <Typography
              sx={{
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                color: '#64748b',
                lineHeight: 1.7,
                mb: 4,
              }}
            >
              {post.excerpt}
            </Typography>

            {/* Author & Meta Row */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              {/* Author */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Tooltip title={tenantName} placement="top">
                  <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    background: `linear-gradient(135deg, ${ACCENT} 0%, #0891b2 100%)`,
                    fontSize: '1rem',
                    fontWeight: 700,
                  }}
                >
                  SD
                </Avatar>
                </Tooltip>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#94a3b8', fontSize: '0.85rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarTodayIcon sx={{ fontSize: 14 }} />
                      {formatDate(post.date)}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 14 }} />
                      {post.readTime}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  sx={{
                    minWidth: 44,
                    height: 44,
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    color: '#64748b',
                    '&:hover': { borderColor: ACCENT, color: ACCENT },
                  }}
                >
                  <ShareIcon sx={{ fontSize: 20 }} />
                </Button>
                <Button
                  sx={{
                    minWidth: 44,
                    height: 44,
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    color: '#64748b',
                    '&:hover': { borderColor: ACCENT, color: ACCENT },
                  }}
                >
                  <BookmarkBorderIcon sx={{ fontSize: 20 }} />
                </Button>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Cover Image */}
      <Container maxWidth="lg" sx={{ mb: { xs: 4, md: 6 } }}>
        <Box
          component={'img'}
          src={post.image}
          sx={{
            width: '100%',
            height: '400px',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            flexShrink: 0,
            border: '1px solid #f1f5f9',
            objectFit: 'cover',
          }}
        />
      </Container>

      {/* Content Area */}
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 300px' },
            gap: { xs: 4, lg: 8 },
            pb: { xs: 6, md: 10 },
          }}
        >
          {/* Main Content */}
          <Box>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Box
                sx={{
                  maxWidth: 720,
                  '& h2': {
                    fontSize: '1.6rem',
                    fontWeight: 700,
                    color: '#0f172a',
                    mt: 6,
                    mb: 2,
                    letterSpacing: '-0.01em',
                  },
                  '& h3': {
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: '#1e293b',
                    mt: 4,
                    mb: 2,
                  },
                  '& p': {
                    fontSize: '1.1rem',
                    color: '#374151',
                    lineHeight: 1.85,
                    mb: 3,
                  },
                  '& ul': {
                    pl: 0,
                    mb: 4,
                    listStyle: 'none',
                  },
                  '& li': {
                    fontSize: '1.05rem',
                    color: '#475569',
                    lineHeight: 1.8,
                    mb: 1.5,
                    pl: 3.5,
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 12,
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: ACCENT,
                    },
                  },
                  '& strong': {
                    color: '#0f172a',
                    fontWeight: 600,
                  },
                }}
                dangerouslySetInnerHTML={{ __html: parseContent(post.content) }}
              />
              {/* Tags */}
              <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #f1f5f9' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label="Dernek Yönetimi" size="small" sx={{ background: '#f1f5f9', color: '#64748b' }} />
                  <Chip label="Dijitalleşme" size="small" sx={{ background: '#f1f5f9', color: '#64748b' }} />
                  <Chip label="Yazılım" size="small" sx={{ background: '#f1f5f9', color: '#64748b' }} />
                </Box>
              </Box>
            </motion.div>
          </Box>

          {/* Sidebar */}
          <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
            <Box sx={{ position: 'sticky', top: 100 }}>
              {/* TOC */}
              {headings.length > 0 && (
                <Box sx={{ mb: 5 }}>
                  <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem', mb: 2 }}>
                    Bu Yazıda
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {headings.map((heading, i) => (
                      <TOCItem 
                        key={i} 
                        title={heading} 
                        active={i === 0} 
                        onClick={() => scrollToHeading(heading)}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Divider sx={{ mb: 4 }} />

              {/* Related */}
              <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem', mb: 2 }}>
                İlgili Yazılar
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(relatedPosts.length > 0 ? relatedPosts : recentPosts).map(p => (
                  <RelatedCard key={p.id} post={p} />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* CTA */}
      <Box sx={{ background: '#0f172a', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: { xs: '1.5rem', md: '2rem' },
                fontWeight: 800,
                color: '#fff',
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Derneğinizi dijitalleştirmeye hazır mısınız?
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '1.1rem',
                mb: 4,
                maxWidth: 500,
                mx: 'auto',
              }}
            >
              14 gün ücretsiz deneyin. Kredi kartı gerekmez, istediğiniz zaman iptal edebilirsiniz.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                component={Link}
                to="/basvuru"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  background: ACCENT,
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  '&:hover': { background: '#02a8e0' },
                }}
              >
                Ücretsiz Başla
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/iletisim"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  borderColor: 'rgba(255,255,255,0.3)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none',
                  '&:hover': { 
                    borderColor: '#fff',
                    background: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Demo Talep Et
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Mobile Related Posts */}
      <Box sx={{ display: { xs: 'block', lg: 'none' }, py: 6, background: '#f8fafc' }}>
        <Container maxWidth="md">
          <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.25rem', mb: 3 }}>
            İlgili Yazılar
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {(relatedPosts.length > 0 ? relatedPosts : recentPosts).map(p => (
              <Box key={p.id} sx={{ background: '#fff', borderRadius: 3, overflow: 'hidden' }}>
                <RelatedCard post={p} />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default BlogDetailPage;
